import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TopBar from '../components/layout/TopBar';
import api from '../utils/api';
import toast from 'react-hot-toast';

export const itemStatus = (item) => {
  if (!item.last_done_date) return { key: 'never', label: 'Not logged yet' };
  const due = new Date(item.last_done_date + 'T00:00:00');
  due.setMonth(due.getMonth() + (item.interval_months || 12));
  const days = Math.round((due.getTime() - Date.now()) / 86400000);
  const dueStr = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (days < 0) return { key: 'overdue', label: 'Overdue since ' + dueStr };
  if (days <= 30) return { key: 'soon', label: 'Due ' + dueStr };
  return { key: 'good', label: 'Due ' + dueStr };
};
export const statusStyle = (key) => ({
  never: { background: '#EAE6F0', color: '#4B3A66' },
  overdue: { background: '#F5D9D6', color: '#8C2B22' },
  soon: { background: '#F5E9CE', color: '#8A6212' },
  good: { background: '#DCEBDF', color: '#1D6B3F' },
}[key] || {});

const TrailersPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('org') || searchParams.get('organization_id') || '';
  const orgQuery = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : '';
  const [trailers, setTrailers] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', trailer_type: 'bumper_pull', year: '', make: '', starter: true });
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/trailers' + orgQuery).then(r => setTrailers(r.data)).catch(() => toast.error('Failed to load trailers'));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error('Give the trailer a name');
    setSaving(true);
    try {
      await api.post('/trailers', { ...form, organization_id: organizationId || undefined });
      toast.success('Trailer added');
      setAdding(false);
      setForm({ name: '', trailer_type: 'bumper_pull', year: '', make: '', starter: true });
      load();
    } catch (e) { toast.error('Failed to add trailer'); }
    finally { setSaving(false); }
  };

  if (!trailers) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <TopBar title="Trailers" subtitle="Maintenance & service log" />
      <div className="page-content">
        {trailers.length === 0 && !adding && (
          <div className="empty-state">
            <div className="empty-state-icon">🛞</div>
            <h3>No trailers yet</h3>
            <p>Add your trailer and get a ready-made maintenance checklist</p>
          </div>
        )}

        {trailers.map(t => {
          const worst = t.items.reduce((w, i) => {
            const k = itemStatus(i).key;
            const rank = { overdue: 3, soon: 2, never: 1, good: 0 };
            return rank[k] > rank[w] ? k : w;
          }, 'good');
          const overdueCount = t.items.filter(i => itemStatus(i).key === 'overdue').length;
          return (
            <div key={t.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => navigate('/trailers/' + t.id + (organizationId ? `?org=${encodeURIComponent(organizationId)}` : ''))}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{t.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {[t.year, t.make, (t.trailer_type || '').replace('_', ' ')].filter(Boolean).join(' ')} · {t.items.length} items tracked
                </div>
              </div>
              <span className="badge" style={{ fontSize: 10, padding: '3px 8px', borderRadius: 8, ...statusStyle(worst) }}>
                {worst === 'overdue' ? overdueCount + ' overdue' : worst === 'soon' ? 'Due soon' : worst === 'never' ? 'Start logging' : 'All good'}
              </span>
              <span style={{ color: 'var(--text-muted)' }}>›</span>
            </div>
          );
        })}

        {!adding && <button className="btn btn-secondary btn-full" style={{ marginTop: 8 }} onClick={() => setAdding(true)}>+ Add a trailer</button>}
        {adding && (
          <div className="card">
            <div className="form-group"><label className="form-label">Trailer name</label>
              <input className="form-input" placeholder="e.g. The 3-horse" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className="form-group" style={{ flex: 1 }}><label className="form-label">Type</label>
                <select className="form-input" value={form.trailer_type} onChange={e => setForm(f => ({ ...f, trailer_type: e.target.value }))}>
                  <option value="bumper_pull">Bumper pull</option>
                  <option value="gooseneck">Gooseneck</option>
                  <option value="stock">Stock</option>
                  <option value="living_quarters">Living quarters</option>
                  <option value="other">Other</option>
                </select></div>
              <div className="form-group" style={{ flex: 1 }}><label className="form-label">Year</label>
                <input className="form-input" value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))} /></div>
            </div>
            <div className="form-group"><label className="form-label">Make / brand</label>
              <input className="form-input" value={form.make} onChange={e => setForm(f => ({ ...f, make: e.target.value }))} /></div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.starter} onChange={e => setForm(f => ({ ...f, starter: e.target.checked }))} style={{ accentColor: 'var(--cr-primary)' }} />
              Start with the standard maintenance checklist (10 items, all editable)
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Adding…' : 'Add trailer'}</button>
              <button className="btn btn-secondary" onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrailersPage;
